// ─── Utilities ───────────────────────────────────────────────────────────────

function getAssetUrl(filename) {
  // window.mmAssets est rendu par layout/theme.liquid et porte les URL
  // produites par asset_url, empreinte comprise. Sans lui on retombe sur une
  // URL NUE, que le CDN Shopify peut garder un an sans jamais la purger : le
  // 27/08/2026 le theme en ligne servait ainsi un config_three_hero.js vieux
  // de cinq jours. Le repli ci-dessous ne subsiste que pour ne rien casser si
  // la table manque ; il faut ajouter une entree dans theme.liquid pour tout
  // nouvel asset charge dynamiquement.
  const connus = typeof window !== 'undefined' && window.mmAssets;
  if (connus && connus[filename]) return connus[filename];

  const scripts = document.getElementsByTagName('script');
  for (const script of scripts) {
    if (script.src && script.src.includes('mm-opti-gsi.js')) {
      return script.src.substring(0, script.src.lastIndexOf('/') + 1) + filename;
    }
  }
  return './' + filename;
}

function loadCss(url) {
  if (Array.from(document.querySelectorAll('head link')).some(el => el.href === url)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.type = 'text/css';
  link.href = url;
  document.head.appendChild(link);
}

function load_scripts(urls) {
  const promises = urls.map(url => {
    if (load_scripts.loaded.has(url)) return Promise.resolve();
    return new Promise(resolve => {
      const script = document.createElement('script');
      script.onload = resolve;
      script.src = url;
      document.head.appendChild(script);
    });
  });
  return Promise.all(promises).then(() => urls.forEach(url => load_scripts.loaded.add(url)));
}
load_scripts.loaded = new Set();

// ─── THREE, une seule fois pour tout le site ─────────────────────────────────
//
// Trois zones en ont besoin (hero 3D, shader FAQ, formulaire contact) et
// chacune injectait SA copie de three.min.js avec son propre Set de dédup :
// jusqu'à deux exécutions de la lib (~600 Ko) sur la même page, constaté sur
// la home et /pages/pos (scan SEO du 21/08/2026). Promesse singleton : le
// premier appelant déclenche l'injection, tous les autres attendent la même.
// La lib est servie depuis les assets du thème (r110 figée) : plus de
// dépendance à cdnjs, même origine que le reste du site.
window.mmLoadThree = (function () {
  let promise = null;
  return function () {
    if (window.THREE) return Promise.resolve();
    if (!promise) {
      promise = load_scripts([getAssetUrl('three.min.js')]);
    }
    return promise;
  };
})();

// ─── Lenis ───────────────────────────────────────────────────────────────────
//
// La lib est chargée par un <script defer> dans theme.liquid, donc elle est là
// avant DOMContentLoaded. Plus de chargement dynamique depuis jsDelivr : le
// scroll ne dépend plus d'une connexion tierce ouverte après le DOM, et les
// premières centaines de millisecondes ne se font plus en scroll natif avant
// reprise.
//
// LE BUG DU FOOTER. Lenis met en cache la hauteur du document et compte sur un
// ResizeObserver posé sur <html> pour la rafraîchir. Le thème contraint <html>
// à height:100% : la boîte observée mesure toujours la hauteur du viewport,
// l'observer ne part jamais, et la limite de scroll reste figée sur la valeur
// relevée au chargement. Ouvrir ou refermer un accordéon (FAQ, routine, fiche
// produit) déplace la hauteur du document de 100 à 600px. Lenis anime alors
// vers une position que le navigateur écrête : l'inertie continue dans le vide
// et le scroll se bloque net sur le footer.
//
// Deux verrous, dans cet ordre :
//   1. html.lenis { height: auto } dans moon-moon.css.liquid — la règle
//      officielle de la lib, qui rend son ResizeObserver opérant ;
//   2. la surveillance de scrollHeight ci-dessous, qui rattrape ce que
//      l'observer manque ou annonce trop tard (son debounce est de 250ms) :
//      accordéon en cours de transition, images tardives, pop-ins Klaviyo,
//      sections rechargées dans l'éditeur de thème.

(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (typeof Lenis === 'undefined') return;
  if (window.lenis) return;

  // LE REGLAGE DU LISSAGE. Sans options, Lenis ne definit ni duration ni lerp
  // — le defaut de lerp est conditionne a duration, donc les DEUX restent
  // indefinis — et c'est le repli interne de la bibliotheque qui decide :
  //
  //     fromTo(from, to, { lerp = 0.1, ... })
  //     value = (1 - s) * value + s * to,  s = 1 - exp(-60 * lerp * dt)
  //
  // lerp 0,1 amortit a 6 par seconde : il faut environ 0,5 s pour que le
  // defilement se pose. Sur une page de lecture ca passe pour une intention,
  // sur un listing qu'on balaye par a-coups cette trainee se lit comme du lag.
  //
  // 0,2 amortit a 12 par seconde, donc environ 0,25 s : le mouvement reste
  // lisse, il colle simplement au geste. C'est LE bouton a tourner si le
  // defilement parait mou (baisser) ou sec (monter). Valeur choisie, pas subie.
  const lenis = new Lenis({ lerp: 0.2 });
  window.lenis = lenis; // exposée : le reste du thème doit pouvoir la piloter

  const root = document.documentElement;
  let lastHeight = -1;
  let lastCheck = 0;
  let pending = true;

  // Une lecture de scrollHeight par frame au maximum : les signaux ne font que
  // lever le drapeau, la mesure se fait dans la boucle rAF.
  function requestSync() {
    pending = true;
  }

  // Le rafraîchissement attend que la hauteur SE POSE : une même valeur lue
  // deux contrôles de suite. Rafraîchir dès qu'elle bouge déclenchait
  // ScrollTrigger.refresh() — un re-layout complet de tous les triggers —
  // deux à trois fois PENDANT l'animation d'un accordéon (implantations du
  // footer, FAQ) : c'était le micro-freeze ressenti à l'ouverture. Une
  // hauteur en mouvement n'a de toute façon pas de limite de scroll fiable
  // à offrir ; on la prend une fois arrivée — au pire un contrôle plus tard
  // (~250ms), le délai qu'avait déjà la surveillance.
  let settling = -1;
  function syncHeight() {
    const height = root.scrollHeight;
    if (height === lastHeight) { settling = -1; return; }
    if (height !== settling) { settling = height; return; }
    settling = -1;
    lastHeight = height;
    lenis.resize();
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  }

  function raf(time) {
    if (pending || time - lastCheck > 250) {
      pending = false;
      lastCheck = time;
      syncHeight();
    }
    lenis.raf(time);
    requestAnimationFrame(raf);
  }
  requestAnimationFrame(raf);

  // Signaux qui n'attendent pas le prochain contrôle périodique. Les trois
  // accordéons du thème animent max-height : transitionend cadre la mesure sur
  // la frame où la hauteur se fixe.
  document.addEventListener('transitionend', function (event) {
    const property = event.propertyName || '';
    if (property.indexOf('height') === -1 && property.indexOf('rows') === -1) return;
    requestSync();
  }, true);
  document.addEventListener('animationend', requestSync, true);
  document.addEventListener('load', requestSync, true); // images, iframes
  window.addEventListener('load', requestSync);
  window.addEventListener('pageshow', requestSync);
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(function (event) {
    document.addEventListener(event, requestSync);
  });

  // Verrou de scroll. .stop-scrolling pose overflow:hidden !important sur
  // <html> (panier, drawer de filtres, modale vidéo). Sans mise en pause,
  // Lenis continue d'écrire scrollTop sur une racine qui ne défile plus : la
  // position est perdue et la page repart en haut à la fermeture. On suit la
  // classe plutôt que d'éditer les quatre endroits qui la posent — tout futur
  // verrou sera couvert d'office.
  new MutationObserver(function () {
    const locked = root.classList.contains('stop-scrolling');
    if (locked === lenis.isStopped) return;
    if (locked) {
      lenis.stop();
    } else {
      lenis.start();
      requestSync(); // .stop-scrolling remet height:100% le temps du verrou
    }
  }).observe(root, { attributes: true, attributeFilter: ['class'] });
})();

// ─── Hero 3D — chargé au DOMContentLoaded ────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  // Rien à faire s'il n'y a pas de canvas sur la page. Sans cette garde, les
  // deux scripts partaient sur TOUTES les pages du site : three.js depuis un
  // CDN (~600 Ko) pour un canvas qui n'existe que sur les sections MM-Hero, et
  // config_three_hero.js qui fait
  //   document.querySelector('.mm-hero-canvas').appendChild(...)
  // sans vérifier — donc un TypeError dans la console de chaque page sans hero,
  // qui masquait les vraies erreurs. Constaté sur /pages/projets-clients.
  if (!document.querySelector('.mm-hero-canvas')) return;

  setTimeout(async () => {
    await window.mmLoadThree();
    await load_scripts([getAssetUrl('config_three_hero.js')]);
  }, 500);
});

// ─── Scripts below-fold — chargés au premier scroll ──────────────────────────

async function loadBelowFoldScripts() {
  // Même garde que le hero 3D dix lignes plus haut : on ne charge que ce que
  // LA page consomme. Sans elle, 142 Ko (Splide + GSAP + ScrollTrigger)
  // partaient au premier scroll de chaque page du site, sliders ou pas.
  // Les marqueurs couvrent tous les consommateurs recensés : les sections à
  // slider rendent toutes un élément .splide, et les deux usages GSAP du
  // thème (mm_anim_gsap.js, MM-Projet-Chiffres) passent par .mm-anim-gsap.
  // Urgence n°3 de l'audit de propreté du 23/08.
  var needsSplide = !!document.querySelector('.splide');
  var needsGsap = !!document.querySelector('.mm-anim-gsap');
  if (!needsSplide && !needsGsap) return;

  // Libs rapatriées dans les assets du thème, versions figées (Splide 4.1.4,
  // GSAP 3.12.5) : les @latest/@4/@3 flottants de jsDelivr et unpkg pouvaient
  // livrer une version différente d'un jour à l'autre, et chaque CDN tiers
  // coûtait une connexion de plus (scan SEO du 21/08/2026).
  if (needsSplide) {
    loadCss(getAssetUrl('splide.min.css'));
    await load_scripts([getAssetUrl('splide.min.js')]);
  }

  if (needsGsap) {
    await load_scripts([
      getAssetUrl('gsap.min.js'),
      getAssetUrl('ScrollTrigger.min.js')
    ]);
    // ScrollTrigger lit la position de scroll sur ses propres écouteurs. Lenis
    // écrit un vrai scrollTop, donc ça marche — mais avec une frame de retard,
    // visible sur les déclenchements. On le branche sur la source.
    if (window.lenis && window.ScrollTrigger) {
      window.lenis.on('scroll', window.ScrollTrigger.update);
      window.ScrollTrigger.refresh();
    }
  }

  var after = [];
  if (needsSplide) after.push(getAssetUrl('mm_splide_testimonials.js'));
  if (needsGsap) after.push(getAssetUrl('mm_anim_gsap.js'));
  await load_scripts(after);
}

window.addEventListener('scroll', loadBelowFoldScripts, { once: true });
